import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, RotateCcw, Search, Trash2 } from 'lucide-react';

import appwrite, { COLLECTIONS } from '../../services/appwrite';
import { dataApi } from '../../services/api';
import { shopLineList_parse as parseList } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import VehicleSectionPicker from '../VehicleSectionPicker';

/**
 * The shop's line sheet, as a table.
 *
 * The insurer guides do not carry every line a shop bills — a cant rail, a boot
 * glass trim, an arch moulding clip — so the platform ships a sheet of them that
 * every organisation gets without configuring anything. This screen is where a
 * shop makes that sheet its own: change a rate, switch off a line it does not
 * use, add its own, and say which insurers each line belongs on.
 *
 * A spreadsheet rather than a list of forms, because that is what it is: a few
 * hundred rows of numbers somebody wants to scan down a column of and correct in
 * place. Every edit saves on its own — there is no Save button to forget — and
 * pasting a block out of Excel into the last row adds that many lines at once.
 *
 * A shop never edits the shared standard row, which belongs to every other shop
 * too. Editing one writes the shop's own override alongside it, and Reset is a
 * delete of that override rather than a second edit: an override that happens to
 * match the standard would silently pin the line if the standard were ever
 * improved.
 *
 * What this screen deliberately does NOT do is decide which lines a quote sees.
 * That resolution — the insurer's guide beats a standard row for the same part,
 * and the shop's decisions beat both — is server-side, in one place, because a
 * catalogue resolved twice is a catalogue resolved wrongly.
 */

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'standard', label: 'Standard' },
  { id: 'edited', label: 'Edited' },
  { id: 'own', label: 'Ours' },
  { id: 'disabled', label: 'Switched off' },
];

const KIND_BADGE = {
  standard: { label: 'Standard', title: 'Shipped with PanelGear. Every shop gets this line.' },
  edited: { label: 'Edited', title: 'A standard line, with your own numbers over the top.' },
  own: { label: 'Ours', title: 'A line you added. It is looked up exactly like a standard one.' },
};

const ZONE_LABELS = {
  front: 'Front', 'front-left': 'Front L', 'front-right': 'Front R',
  left: 'Left', right: 'Right',
  rear: 'Rear', 'rear-left': 'Rear L', 'rear-right': 'Rear R',
  roof: 'Roof', underside: 'Underside', other: 'Other',
};

/** The columns a paste out of a spreadsheet fills, left to right. */
const PASTE_COLUMNS = [
  'lineName', 'quoteCategory',
  'removeRefitHoursStandard', 'repairHoursStandard', 'paintUnits',
  'searchTerms', 'notes',
];

const BLANK_ROW = {
  lineName: '', quoteCategory: '', searchTerms: '', notes: '',
  removeRefitHoursStandard: '', repairHoursStandard: '', paintUnits: '',
  locations: [], insurerConfigIds: [], appliesToPrivate: true,
  electricVehicleOnly: false,
};

/** What a line is worth showing as, with any override already folded on. */
function effective(row) {
  if (!row.override) return row;
  const merged = { ...row };
  for (const [key, value] of Object.entries(row.override)) {
    if (value === null || value === undefined || value === '') continue;
    if (['$id', 'organisationId', 'standardLineId', 'active'].includes(key)) continue;
    merged[key] = value;
  }
  // The override row carries the column's default false whatever the shop
  // meant, so it cannot be what decides. Electric-only is a fact about the part:
  // a shop can add it to a line, never take it off a standard one. Mirrors the
  // resolver in org-lines-core.js, so this screen and a quote agree.
  merged.electricVehicleOnly = row.electricVehicleOnly === true || row.override.electricVehicleOnly === true;
  return merged;
}

/** Blank rather than a misleading 0, matching the rest of the quote screens. */
const numText = (v) => (v === 0 || v == null || v === '' ? '' : String(v));

/**
 * A cell that saves when you leave it or press Enter, not on every keystroke.
 *
 * Per-keystroke would be one write per letter of a part name. Escape puts the
 * old value back, which is the only way out of a half-typed edit that does not
 * commit it.
 */
function EditableCell({ value, onCommit, disabled, className = '', ...props }) {
  const [draft, setDraft] = useState(value ?? '');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty) setDraft(value ?? ''); }, [value, dirty]);

  const commit = () => {
    setDirty(false);
    if (String(draft ?? '') === String(value ?? '')) return;
    onCommit(draft);
  };

  return (
    <Input
      value={draft}
      disabled={disabled}
      onChange={(e) => { setDirty(true); setDraft(e.target.value); }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur(); }
        if (e.key === 'Escape') { setDirty(false); setDraft(value ?? ''); e.currentTarget.blur(); }
      }}
      className={`h-8 border-transparent bg-transparent hover:border-input focus:border-input px-1.5 ${className}`}
      {...props}
    />
  );
}

/** The sections a line belongs to, chosen on the same birds-eye view as everywhere else. */
function SectionsCell({ locations, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const zones = parseList(locations);
  const label = zones.length === 0
    ? 'Any'
    : zones.length <= 2
      ? zones.map((z) => ZONE_LABELS[z] || z).join(', ')
      : `${zones.length} sections`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="h-8 w-full px-1.5 text-left text-sm rounded-md border border-transparent hover:border-input disabled:opacity-50 truncate"
          title={zones.length ? zones.map((z) => ZONE_LABELS[z] || z).join(', ') : 'Anywhere on the vehicle'}
        >
          <span className={zones.length ? '' : 'text-muted-foreground'}>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="text-xs font-medium text-muted-foreground mb-2">
          Sections this line can be quoted in
        </div>
        <VehicleSectionPicker multiple values={zones} onChange={onChange} />
        <div className="mt-3 flex items-center justify-between gap-2">
          {/* No section is a real answer, not an unfinished one: frame tape and
              a hub cap are not about a panel at all. */}
          <span className="text-xs text-muted-foreground">
            {zones.length === 0 ? 'Anywhere' : `${zones.length} selected`}
          </span>
          <Button size="sm" variant="ghost" onClick={() => onChange([])} disabled={zones.length === 0}>
            Anywhere
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Which insurers a line appears on, and whether it appears on private work.
 *
 * The list is insurers that have a rate guide behind them, because the guide's
 * config id is what a job is actually scoped against — naming one without a
 * guide would narrow the line to a key no job ever carries, which reads as "I
 * chose an insurer and the line vanished". Insurers with no guide are quoted as
 * private work, so the checkbox above covers them; the footnote says so rather
 * than leaving eight familiar names mysteriously absent.
 */
function InsurersCell({ insurerConfigIds, appliesToPrivate, insurers, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const chosen = parseList(insurerConfigIds);
  const isPrivate = appliesToPrivate !== false;
  const all = insurers.map((i) => i.insurerConfigId);

  const label = chosen.length === 0
    ? (isPrivate ? 'All' : 'All insurers')
    : `${chosen.length} insurer${chosen.length === 1 ? '' : 's'}${isPrivate ? ' + private' : ''}`;

  const set = (ids) => onChange({ insurerConfigIds: ids, appliesToPrivate: isPrivate });

  // An empty list means every insurer, so every box is ticked. Un-ticking one
  // from that state has to mean "all except this", not "only this" — otherwise
  // one click on a full list silently throws the other nine away.
  const toggle = (configId) => {
    if (chosen.length === 0) { set(all.filter((id) => id !== configId)); return; }
    const next = new Set(chosen);
    if (next.has(configId)) next.delete(configId); else next.add(configId);
    // Ticking the last one back on is "all" again, which is the state that
    // keeps working when a new insurer is added later.
    set(next.size === all.length ? [] : [...next]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="h-8 w-full px-1.5 text-left text-sm rounded-md border border-transparent hover:border-input disabled:opacity-50 truncate"
          title={chosen.length === 0
            ? `Every insurer${isPrivate ? ' and private work' : ''}`
            : insurers.filter((i) => chosen.includes(i.insurerConfigId)).map((i) => i.name).join(', ')}
        >
          <span className={chosen.length === 0 && isPrivate ? 'text-muted-foreground' : ''}>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="text-xs font-medium text-muted-foreground mb-2">Appears on</div>

        <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
          {/* Private work is not an insurer with an empty id — it is a job with
              no insurer at all, where these rates are the only rates there are.
              So it is its own answer rather than an entry in the list. */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={isPrivate}
              onCheckedChange={(on) => onChange({ insurerConfigIds: chosen, appliesToPrivate: !!on })}
            />
            <span className="font-medium">Private work</span>
          </label>
          <div className="h-px bg-border my-1.5" />
          {insurers.map((ins) => (
            <label key={ins.insurerConfigId} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={chosen.length === 0 || chosen.includes(ins.insurerConfigId)}
                onCheckedChange={() => toggle(ins.insurerConfigId)}
              />
              <span className="truncate">{ins.name}</span>
            </label>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {chosen.length === 0 ? 'Every insurer' : `${chosen.length} of ${all.length}`}
          </span>
          <Button size="sm" variant="ghost" onClick={() => set([])} disabled={chosen.length === 0}>
            All insurers
          </Button>
        </div>

        <p className="mt-2 text-[11px] leading-snug text-muted-foreground border-t pt-2">
          Only insurers with a rate guide can be named here. Work for any other insurer is quoted at
          your own rates, so it is covered by <strong>Private work</strong>.
        </p>
      </PopoverContent>
    </Popover>
  );
}

export default function ShopLineSettings() {
  const [lines, setLines] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [newRow, setNewRow] = useState(BLANK_ROW);
  const [adding, setAdding] = useState(false);
  const savedTimer = useRef(null);
  const newNameRef = useRef(null);
  // Row ids this screen has just written, so the realtime echo of our own edit
  // does not reload the table out from under whatever is being typed next.
  const ownWrites = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const [rows, insurerList] = await Promise.all([
        dataApi.shopLineList(),
        dataApi.insurerList().catch(() => []),
      ]);
      rows.sort((a, b) => (a.lineName || '').localeCompare(b.lineName || ''));
      setLines(rows);
      // Only insurers that actually have a guide behind them are worth naming:
      // scoping a line to one with no config would narrow it to nothing.
      const seen = new Set();
      setInsurers(
        (insurerList || [])
          .filter((i) => i.insurerConfigId && !seen.has(i.insurerConfigId) && seen.add(i.insurerConfigId))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
    } catch (err) {
      console.error('Failed to load the shop line sheet', err);
      toast.error(err.message || 'Could not load your line sheet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  /**
   * Somebody else's change to the sheet, while this screen is open.
   *
   * The sheet belongs to the shop, not to whoever opened the screen, so two
   * people tidying it at once is the normal case rather than an edge one — and
   * without this the second one to save overwrites what the first could not see.
   *
   * A write of our own is skipped: `edit` has already patched the row on screen
   * and reloading on the echo would drop a half-typed cell somewhere else in the
   * table.
   */
  useEffect(() => {
    const unsubscribe = appwrite.subscribeToCollection(COLLECTIONS.ORG_LINE_ALLOWANCES, (response) => {
      const id = response.payload?.$id;
      if (id && ownWrites.current.has(id)) { ownWrites.current.delete(id); return; }
      load();
    });
    return () => { try { unsubscribe?.(); } catch { /* already closed */ } };
  }, [load]);

  const flashSaved = (id) => {
    setSavedId(id);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSavedId(null), 1200);
  };

  /**
   * Apply a change now, on screen, and reconcile with the server after.
   *
   * The row is patched locally first so a table of a few hundred lines does not
   * blink on every keystroke committed. A failed write reloads, which puts the
   * true value back rather than leaving the screen claiming something that was
   * never saved.
   */
  const edit = async (row, patch, localPatch = patch) => {
    setBusyId(row.$id);
    setLines((prev) => prev.map((l) => (l.$id === row.$id
      ? { ...l, ...(l.kind === 'own' ? localPatch : { override: { ...(l.override || {}), ...localPatch } }) }
      : l)));
    try {
      const written = row.kind === 'own'
        ? await dataApi.shopLineUpdate(row, patch)
        : await dataApi.shopLineOverride(row, { ...effective(row), ...patch });
      if (written?.$id) ownWrites.current.add(written.$id);
      flashSaved(row.$id);
      // An edit to a standard turns it into an override, so the row's kind
      // changes — reload rather than guess at what the server now holds.
      if (row.kind === 'standard') await load();
    } catch (err) {
      console.error('Shop line sheet write failed', err);
      toast.error(err.message || 'Could not save that');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const run = async (work, message) => {
    try {
      await work();
      await load();
      if (message) toast.success(message);
    } catch (err) {
      console.error('Shop line sheet write failed', err);
      toast.error(err.message || 'Could not save that');
    }
  };

  const addRow = async (values) => {
    if (!String(values.lineName || '').trim()) return;
    setAdding(true);
    try {
      await dataApi.shopLineCreate(values);
      setNewRow(BLANK_ROW);
      await load();
      // Back to the name field, so a run of lines can be typed without
      // reaching for the mouse between each one.
      if (document.activeElement === document.body) newNameRef.current?.focus();
    } catch (err) {
      console.error('Could not add the line', err);
      toast.error(err.message || 'Could not add that line');
    } finally {
      setAdding(false);
    }
  };

  /**
   * A block pasted out of a spreadsheet becomes that many lines.
   *
   * Tab-separated columns in PASTE_COLUMNS order, newline-separated rows, which
   * is what Excel and Sheets both put on the clipboard. A single cell with no
   * tabs or newlines is an ordinary paste and is left to the input.
   */
  const handlePaste = async (event) => {
    const text = event.clipboardData?.getData('text/plain') || '';
    if (!/[\t\n]/.test(text.trim())) return;
    event.preventDefault();

    const rows = text
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line) => line.split('\t'))
      .filter((cells) => cells.some((c) => c.trim()));

    // A pasted header row is the commonest mistake and the easiest to spot.
    if (rows.length && /^(line ?name|name|description)$/i.test((rows[0][0] || '').trim())) rows.shift();

    const values = rows.map((cells) => {
      const row = { ...BLANK_ROW };
      PASTE_COLUMNS.forEach((key, i) => { row[key] = (cells[i] ?? '').trim(); });
      return row;
    }).filter((row) => row.lineName);

    if (values.length === 0) {
      toast.error('Nothing in that paste had a line name');
      return;
    }

    setAdding(true);
    try {
      const { created, failed } = await dataApi.shopLineCreateMany(values);
      await load();
      if (failed.length === 0) toast.success(`${created.length} lines added`);
      else toast.warning(`${created.length} added, ${failed.length} could not be: ${failed[0].error}`);
    } catch (err) {
      toast.error(err.message || 'Could not add those lines');
    } finally {
      setAdding(false);
    }
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return lines.filter((line) => {
      if (filter === 'disabled' ? !line.disabled : filter !== 'all' && line.kind !== filter) return false;
      if (!term) return true;
      return `${line.lineName} ${line.searchTerms || ''}`.toLowerCase().includes(term);
    });
  }, [lines, search, filter]);

  const changed = useMemo(
    () => lines.filter((l) => l.kind !== 'standard' || l.disabled).length,
    [lines]
  );

  if (loading) {
    return <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
  }

  const setNew = (patch) => setNewRow((prev) => ({ ...prev, ...patch }));

  return (
    // One column that does not itself scroll — only the table body inside it
    // does. Everything above and below the table is `shrink-0`, so the panel
    // never grows past the viewport and produces a second scrollbar beside the
    // table's own.
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      <div className="flex items-center gap-3 shrink-0">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            {FILTERS.map((f) => (
              <TabsTrigger key={f.id} value={f.id}>{f.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {/* Narrow, and to the right: it refines a list you can already see
            rather than being the way in to it. */}
        <div className="relative ml-auto w-56">
          <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="pl-8 h-9"
          />
        </div>
      </div>

      <div className="rounded-md border flex-1 min-h-0 overflow-hidden">
        <Table containerClassName="h-full">
          <TableHeader sticky>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[22rem] min-w-[16rem]">Line</TableHead>
              <TableHead className="w-32">Category</TableHead>
              <TableHead className="w-28">Sections</TableHead>
              <TableHead className="w-36">Appears on</TableHead>
              <TableHead className="w-20 text-right">R&amp;R hrs</TableHead>
              <TableHead className="w-20 text-right">Repair hrs</TableHead>
              <TableHead className="w-20 text-right">Paint</TableHead>
              <TableHead className="w-14 text-center" title="Only offered on electric vehicles">EV only</TableHead>
              <TableHead className="w-16 text-center">On</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>

          <TableBody>
            {visible.map((row) => {
              const shown = effective(row);
              const badge = KIND_BADGE[row.kind];
              const busy = busyId === row.$id;
              const num = (key) => (v) => edit(row, { [key]: v === '' ? null : Number(v) });
              return (
                <TableRow key={row.$id} className={row.disabled ? 'opacity-55' : undefined}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <EditableCell
                        value={shown.lineName}
                        onCommit={(v) => edit(row, { lineName: v })}
                        className={row.disabled ? 'line-through' : 'font-medium'}
                      />
                      <span
                        className="shrink-0 text-[9px] uppercase tracking-wide text-muted-foreground border rounded px-1 leading-4"
                        title={badge.title}
                      >
                        {badge.label}
                      </span>
                      {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />}
                      {savedId === row.$id && !busy && <Check className="h-3 w-3 text-emerald-600 shrink-0" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <EditableCell value={shown.quoteCategory} onCommit={(v) => edit(row, { quoteCategory: v })} />
                  </TableCell>
                  <TableCell>
                    <SectionsCell
                      locations={shown.locations}
                      onChange={(zones) => edit(row, { locations: zones })}
                    />
                  </TableCell>
                  <TableCell>
                    <InsurersCell
                      insurerConfigIds={shown.insurerConfigIds}
                      appliesToPrivate={shown.appliesToPrivate}
                      insurers={insurers}
                      onChange={(patch) => edit(row, patch)}
                    />
                  </TableCell>
                  <TableCell>
                    <EditableCell type="number" step="0.05" min="0" className="text-right"
                      value={numText(shown.removeRefitHoursStandard)} onCommit={num('removeRefitHoursStandard')} />
                  </TableCell>
                  <TableCell>
                    <EditableCell type="number" step="0.05" min="0" className="text-right"
                      value={numText(shown.repairHoursStandard)} onCommit={num('repairHoursStandard')} />
                  </TableCell>
                  <TableCell>
                    <EditableCell type="number" step="0.05" min="0" className="text-right"
                      value={numText(shown.paintUnits)} onCommit={num('paintUnits')} />
                  </TableCell>
                  <TableCell className="text-center">
                    {/* A standard line that is electric-only stays that way: the
                        part does not exist on any other car. */}
                    <Checkbox
                      checked={shown.electricVehicleOnly === true}
                      disabled={row.kind !== 'own' && row.electricVehicleOnly === true}
                      onCheckedChange={(on) => edit(row, { electricVehicleOnly: on === true })}
                      title={row.kind !== 'own' && row.electricVehicleOnly === true
                        ? 'Standard EV line — only offered on electric vehicles'
                        : 'Only offer this line on electric vehicles'}
                      aria-label="Only offer this line on electric vehicles"
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={!row.disabled}
                      onCheckedChange={(on) => run(
                        () => dataApi.shopLineSetActive(row, on),
                        on ? 'Line switched on' : 'Line switched off for your shop'
                      )}
                      aria-label={row.disabled ? 'Switch this line on' : 'Switch this line off'}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-0.5">
                      {row.kind === 'edited' && (
                        <Button
                          size="sm" variant="ghost" className="h-8 w-8 p-0"
                          title="Drop your changes and go back to the standard line"
                          onClick={() => run(() => dataApi.shopLineResetToStandard(row), 'Back to the standard line')}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {row.kind === 'own' && (
                        <Button
                          size="sm" variant="ghost"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                          title="Remove this line"
                          onClick={() => run(() => dataApi.shopLineDelete(row), 'Line removed')}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* The new-line row. A name is the whole of what a line needs, so
                naming one adds it: on Enter, or as soon as focus leaves the row.
                Leaving the ROW rather than the name field is deliberate — adding
                the moment the name field blurred would fire on the way to the
                category or the section picker beside it, so those cells could
                never be filled in before the row existed. Pasting a block out of
                a spreadsheet adds that many at once. */}
            <TableRow
              className="bg-muted/30 hover:bg-muted/40"
              onBlur={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                addRow(newRow);
              }}
            >
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Input
                    value={newRow.lineName}
                    onChange={(e) => setNew({ lineName: e.target.value })}
                    onPaste={handlePaste}
                    ref={newNameRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addRow(newRow);
                      // Escape abandons a half-typed line rather than adding it
                      // on the way out of the row.
                      if (e.key === 'Escape') { setNewRow(BLANK_ROW); e.currentTarget.blur(); }
                    }}
                    placeholder="Add a line item…"
                    className="h-8 px-1.5"
                    disabled={adding}
                  />
                  {adding && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />}
                </div>
              </TableCell>
              <TableCell>
                <Input value={newRow.quoteCategory} onChange={(e) => setNew({ quoteCategory: e.target.value })}
                  placeholder="Category" className="h-8 px-1.5" disabled={adding} />
              </TableCell>
              <TableCell>
                <SectionsCell locations={newRow.locations} onChange={(zones) => setNew({ locations: zones })} disabled={adding} />
              </TableCell>
              <TableCell>
                <InsurersCell
                  insurerConfigIds={newRow.insurerConfigIds}
                  appliesToPrivate={newRow.appliesToPrivate}
                  insurers={insurers}
                  onChange={(patch) => setNew(patch)}
                  disabled={adding}
                />
              </TableCell>
              {['removeRefitHoursStandard', 'repairHoursStandard', 'paintUnits'].map((key) => (
                <TableCell key={key}>
                  <Input type="number" step="0.05" min="0" value={newRow[key]}
                    onChange={(e) => setNew({ [key]: e.target.value })}
                    placeholder="—" className="h-8 px-1.5 text-right" disabled={adding} />
                </TableCell>
              ))}
              <TableCell className="text-center">
                <Checkbox
                  checked={newRow.electricVehicleOnly === true}
                  onCheckedChange={(on) => setNew({ electricVehicleOnly: on === true })}
                  disabled={adding}
                  title="Only offer this line on electric vehicles"
                  aria-label="Only offer this line on electric vehicles"
                />
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>

            {visible.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">
                  {search ? 'Nothing matches that.' : 'No lines here yet — add one below.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* A footnote, not a headline: worth having, not worth the top of the
          screen. */}
      <div className="flex justify-end shrink-0">
        <span className="text-xs text-muted-foreground">
          {visible.length} of {lines.length}{changed > 0 ? ` · ${changed} changed by you` : ''}
        </span>
      </div>
    </div>
  );
}
